import { useEffect, useRef } from "react"
import {
    ICancelablePromise,
    cancelablePromise as cancelablePromiseFunc
} from "services/Utils";

// Hooks wrapper for cancelablePromise.
// Mostly from: https://medium.com/@rajeshnaroth/writing-a-react-hook-to-cancel-promises-when-a-component-unmounts-526efabf251f
export function useCancelablePromise(): {cancelablePromise: <T>(p: Promise<T>) => Promise<T>} {
    // Using a ref to persist promises across rerenders.
    const promisesRef = useRef<ICancelablePromise<any>[]>([]);
    const promises = promisesRef.current;

    useEffect(() => {
        // On unmount, this will cancel all pending promises.
        return function cancelAll() {
            promises.forEach(p => p.cancel());
        }
    }, [])

    function cancelablePromise<T>(p: Promise<T>): Promise<T> {
        const cancelable = cancelablePromiseFunc(p);
        promises.push(cancelable);

        // When the original promise finishes, remove it from our array.
        // The original promise can't be canceled, so this is guaranteed to run.
        const removeDonePromise = () => {
            const i = promises.indexOf(cancelable);
            if (i !== -1) {
                promises.splice(i, 1);
            } else {
                console.warn('Cancelable promise missing from promises array');
            }
        }

        // NOTE: We're not using "finally" here because it creates a promise branch,
        // which won't catch rejections, causing them to bubble up to the top level.
        p.then(removeDonePromise, removeDonePromise);

        return cancelable.promise;
    }

    return {
        cancelablePromise
    }
}